import clsx from 'clsx';
import {
  Children,
  forwardRef,
  type ReactNode,
  useMemo,
  useRef,
  cloneElement,
} from 'react';
import { useIntl } from 'react-intl';

import { Button } from '@dynatrace/strato-components/buttons';
import {
  type DataTestId,
  type MaskingProps,
  type StylingProps,
} from '@dynatrace/strato-components/core';
import Colors from '@dynatrace/strato-design-tokens/colors';

import {
  breadcrumbsContainerCSS,
  breadcrumbsItemInvisibleCSS,
} from './Breadcrumbs.sty.js';
import { isBreadcrumbItem, Item } from './Item.js';
import { useContainerSize } from '../../core/hooks/useContainerSize.js';
import { useMeasureChildren } from '../../core/hooks/useMeasureChildren.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { Menu } from '../menu/Menu.js';

//#region Typings
/**
 * Accepted properties for Breadcrumbs
 * @public
 */
export interface BreadcrumbsProps
  extends StylingProps,
    DataTestId,
    MaskingProps {
  /** Elements to be displayed as Breadcrumbs. */
  children?: ReactNode;
}

type SlotsType = {
  items: ReactNode[];
  menuItems: ReactNode[];
};
// #endregion

/** Breadcrumbs component */
const BreadcrumbsRoot = /* @__PURE__ */ forwardRef<
  HTMLOListElement,
  BreadcrumbsProps
>((props: BreadcrumbsProps, forwardedRef) => {
  const {
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  }: BreadcrumbsProps = props;
  const _containerRef = useRef<HTMLOListElement | null>(null);
  const { containerRef, measurements, childProps } =
    useMeasureChildren<HTMLOListElement>();
  const { width: containerWidth } = useContainerSize(_containerRef);

  const rootRef = useMergeRefs([forwardedRef, _containerRef, containerRef]);
  const overflowingBreadcrumbs = useRef(new Map<string, boolean>());
  const intl = useIntl();

  const slots = useMemo(() => {
    const slots: SlotsType = { items: [], menuItems: [] };
    const childrenArray = Children.toArray(children);

    // Get total width of children.
    let childWidths = 0;

    for (const value of Object.values(measurements)) {
      childWidths += value?.width || 0;
    }

    // As soon as we have sufficient data from the measurement hook,
    // determine visible and hidden breadcrumbs.
    if (Object.keys(measurements).length === childProps.length) {
      const childKeys = childProps.map((props) => props['data-id']);

      // Index of the next child to remove if there is not enough space.
      // We start collapsing at the second breadcrumb.
      // If an item is already collapsed, we additionally skip the menu
      // toggle that is then added at the second position.
      let childIndex = overflowingBreadcrumbs.current.size ? 2 : 1;

      // Clear all entries so that breadcrumbs will show up again when expanding the container.
      overflowingBreadcrumbs.current.clear();

      while (childWidths > containerWidth && childIndex < childProps.length) {
        // As long as at least three breadcrumbs are still visible (root + location + current),
        // we keep removing location breadcrumbs until only root and current are left.
        if (childIndex !== childProps.length - 1) {
          const key = childKeys[childIndex];
          if (key) {
            overflowingBreadcrumbs.current.set(key, true);
            childWidths -= measurements[key]?.width || 0;
          }
        } else {
          // If only the root and current breadcrumb are visible, the next one to collapse is
          // the root breadcrumb. The current breadcrumb will only get a text ellipsis.
          const key = childKeys[0];
          if (key) {
            overflowingBreadcrumbs.current.set(key, true);
            childWidths -= measurements[key]?.width || 0;
          }
        }
        childIndex++;
      }
    }

    // Iterate over children and add them to the corresponding slots.
    for (const [index, child] of childrenArray.entries()) {
      if (isBreadcrumbItem(child)) {
        const uniqueKey = `nav-items-${index}`;

        // If a breadcrumb is hidden already, we add the toggle for the overlay after the first breadcrumb.
        // We need to account for the additional child when spreading the childProps onto each child and
        // shift the index by one for each child after the button.
        const adjustedIndex =
          overflowingBreadcrumbs.current.size > 0 && index > 0
            ? index + 1
            : index;

        // The last breadcrumb should not render the divider.
        const isLast = index === childrenArray.length - 1;

        slots.items.push(
          cloneElement(child, {
            ...child.props,
            key: uniqueKey,
            className: clsx(
              {
                [`${breadcrumbsItemInvisibleCSS}`]:
                  overflowingBreadcrumbs.current.get(
                    childProps[adjustedIndex]?.['data-id'] || '',
                  ),
              },
              child.props.className,
            ),
            isLast,
            isLastVisible:
              isLast &&
              overflowingBreadcrumbs.current.size === childrenArray.length - 1,
            isCollapsed: false,
            ...childProps[adjustedIndex],
          }),
        );

        if (
          overflowingBreadcrumbs.current.get(
            childProps[adjustedIndex]?.['data-id'] || '',
          )
        ) {
          slots.menuItems.push(
            cloneElement(<Menu.Link />, {
              ...child.props,
              key: uniqueKey,
              isCollapsed: true,
              ...childProps[adjustedIndex],
            }),
          );
        }
      }
    }

    return slots;
  }, [children, measurements, containerWidth, childProps]);

  return (
    <nav
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      aria-label={intl.formatMessage({
        defaultMessage: 'Breadcrumb',
        id: 'x2bmZRg98b0KodlW',
      })}
      className={consumerClassName}
      style={consumerStyle}
      {...remainingProps}
    >
      <ol ref={rootRef} className={breadcrumbsContainerCSS}>
        {slots.items[0]}
        {overflowingBreadcrumbs.current.size > 0 && (
          <Menu>
            <Menu.Trigger>
              <Item as={Button} {...childProps[1]} menuTrigger size="condensed">
                <Button.Prefix style={{ color: Colors.Text.Neutral.Default }}>
                  …
                </Button.Prefix>
              </Item>
            </Menu.Trigger>
            <Menu.Content>{slots.menuItems}</Menu.Content>
          </Menu>
        )}
        {slots.items.slice(1)}
      </ol>
    </nav>
  );
});

(
  BreadcrumbsRoot as typeof BreadcrumbsRoot & { displayName: string }
).displayName = 'Breadcrumbs';

/**
 * The `Breadcrumbs` component can be used to indicate the
 * location and hierarchy of the current page. It also allows
 * users to easily navigate back to a higher level in the hierarchy.
 * @public
 */
export const Breadcrumbs = Object.assign(BreadcrumbsRoot, {
  Item,
});
